import React, { useState } from 'react';
import { Box, Button, Chip, CircularProgress, ListItemText } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ArrowDropDown } from '@mui/icons-material';
import { TenantUserSettings } from '@/api/generatedApi';
import { SearchPageObjectModel } from '@/poms/searchPageObjectModel';
import { RoundedPopover, RoundedPopoverList, RoundedPopoverListItem } from '@/components/Popover/RoundedPopover';
import { SearchTabCounts } from '../types';

const MenuButton = styled(Button)(({ theme }) => ({
  minHeight: '40px',
  height: '40px',
  padding: '0 12px 0 16px',
  borderRadius: '8px',
  textTransform: 'none',
  fontSize: '14px',
  fontWeight: 500,
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.divider,
  },
}));

const CountChip = styled(Chip)(() => ({
  height: '18px',
  fontSize: '11px',
  fontWeight: 500,
  marginLeft: 'auto',
  minWidth: '24px',
  '& .MuiChip-label': {
    padding: '0 6px',
  },
}));

interface SearchTenantMenuProperties {
  tenants: TenantUserSettings[];
  resultCounts?: SearchTabCounts;
  loadingStates?: Record<string, boolean>;
  onSelectTenant: (tenant: TenantUserSettings) => void;
}

export const SearchTenantMenu = ({
  tenants,
  resultCounts,
  loadingStates,
  onSelectTenant,
}: SearchTenantMenuProperties) => {
  const [anchorElement, setAnchorElement] = useState<HTMLButtonElement | undefined>();
  const isOpen = Boolean(anchorElement);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElement(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorElement(undefined);
  };

  const handleSelectTenant = (tenant: TenantUserSettings) => {
    onSelectTenant(tenant);
    handleClose();
  };

  if (tenants.length === 0) {
    return;
  }

  return (
    <>
      <MenuButton
        data-testid={SearchPageObjectModel.SearchTenantMenuButton}
        onClick={handleClick}
        endIcon={<ArrowDropDown />}
      >
        Add tenant
      </MenuButton>
      <RoundedPopover
        data-testid={SearchPageObjectModel.SearchTenantMenu}
        open={isOpen}
        anchorEl={anchorElement}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              marginTop: '4px',
              borderRadius: '8px',
              minWidth: '200px',
              maxHeight: '300px',
            },
          },
        }}
      >
        <RoundedPopoverList>
          {tenants.map(tenant => {
            const tenantId = tenant?.tenantId ?? '';
            const isLoading = loadingStates?.[tenantId];
            const count = resultCounts?.[tenantId];

            return (
              <RoundedPopoverListItem key={tenantId} onClick={() => handleSelectTenant(tenant)}>
                <ListItemText primary={tenant?.tenantDisplayName} />
                {isLoading ? (
                  <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
                    <CircularProgress size={16} thickness={4} sx={{ color: 'text.secondary' }} />
                  </Box>
                ) : (
                  count !== undefined && <CountChip size='small' label={count} />
                )}
              </RoundedPopoverListItem>
            );
          })}
        </RoundedPopoverList>
      </RoundedPopover>
    </>
  );
};
